import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import { CreateTemplateRequest } from '../../model/composicao/TemplateComposicao';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import { TemplateComposicaoViewModel } from '../../viewmodel/composicao/TemplateComposicaoViewModel';
import { TipoBancaViewModel } from '../../viewmodel/banca/TipoBancaViewModel';
import { VinculoViewModel } from '../../viewmodel/VinculoViewModel';
import { PapelMembroViewModel } from '../../viewmodel/PapelMembroViewModel';
import Joyride, { CallBackProps, STATUS } from 'react-joyride';
import { BsQuestionCircle } from 'react-icons/bs';


const TOUR_STEPS = [
    {
        target: '[data-tour="info-basicas"]',
        content: 'Comece selecionando a categoria do processo (ex: Aluno Graduação, Servidor). Depois defina o nome, tipo de banca e uma descrição opcional.',
        disableBeacon: true,
    },
    {
        target: '[data-tour="regras-vinculos"]',
        content: 'Defina quantos membros titulares de cada vínculo a banca deve ter. Ex: mínimo 1 e máximo 1 Docente. Use "Adicionar" para incluir novos vínculos.',
    },
    {
        target: '[data-tour="regras-papeis"]',
        content: 'Configure os papéis extras da banca: Técnico e Secretário. Defina o mínimo e máximo de cada papel.',
    },
    {
        target: '[data-tour="vigencia"]',
        content: 'Opcionalmente, defina um período de vigência. Templates expirados não serão usados na criação de novas bancas.',
    },
    {
        target: '[data-tour="botoes-acao"]',
        content: 'Clique em "Criar Template" para salvar. Se o tipo de banca for "Todos", o sistema cria automaticamente um template para Facial, Virtual e Recursal.',
    },
];

const NovoTemplate: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [salvando, setSalvando] = useState(false);
    const [tiposBanca, setTiposBanca] = useState<any[]>([]);
    const [vinculos, setVinculos] = useState<any[]>([]);
    const [papeis, setPapeis] = useState<any[]>([]);
    const categoriaRef = useRef<HTMLSelectElement>(null);

    const [form, setForm] = useState<CreateTemplateRequest>({
        nome: '',
        descricao: '',
        tipoBancaId: 'TODOS',
        categoria: '',
        dataInicio: undefined,
        dataFim: undefined,
        regrasVinculos: [],
        regrasPapeis: []
    });
    const [mostrarExpiracao, setMostrarExpiracao] = useState(false);
    const [runTour, setRunTour] = useState(false);

    const handleTourCallback = (data: CallBackProps) => {
        if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
            setRunTour(false);
        }
    };

    useEffect(() => {
        setBreadcrumbs(createBreadcrumbs.fromRoot([
            { path: '/composicoes', breadcrumb: 'Composições' },
            { path: '/composicoes/novo', breadcrumb: 'Novo Template' }
        ]));
        carregarDados();
    }, [setBreadcrumbs]);

    useEffect(() => {
        const categoriaParam = searchParams.get('categoria');
        if (categoriaParam) {
            setForm(prev => ({ ...prev, categoria: categoriaParam, nome: getCategoriaLabel(categoriaParam) + ' - Básico' }));
        }
    }, [searchParams]);

    useEffect(() => {
        if (categoriaRef.current && !form.categoria && tiposBanca.length > 0) {
            categoriaRef.current.focus();
        }
    }, [form.categoria, tiposBanca]);

    const carregarDados = async () => {
        try {
            const [tiposData, vinculosData, papeisData] = await Promise.all([
                TipoBancaViewModel.getTiposBanca(),
                VinculoViewModel.getVinculos(),
                PapelMembroViewModel.getPapeisMembro()
            ]);
            setTiposBanca(tiposData);
            setVinculos(vinculosData);
            const papeisFiltered = papeisData.filter(p => p.nome !== 'Titular');
            setPapeis(papeisFiltered);
            
            // Preencher regras padrão de vínculos
            const vinculosPadrao = ['Docente', 'Estudante da Graduação', 'Estudante da Pós-Graduação', 'Servidor', 'Representante da Sociedade Civil Organizada'];
            const regrasVinculos = vinculosData
                .filter(v => vinculosPadrao.includes(v.nome))
                .map(v => ({ vinculoId: v.id as string, minimo: 1, maximo: 1 }));
            
            // Preencher regras padrão de papéis
            const tecnico = papeisFiltered.find(p => p.nome === 'Técnico');
            const secretario = papeisFiltered.find(p => p.nome === 'Secretário');
            const regrasPapeis: Array<{ papelMembroId: string; minimo: number; maximo: number }> = [];
            if (tecnico) regrasPapeis.push({ papelMembroId: tecnico.id, minimo: 1, maximo: 2 });
            if (secretario) regrasPapeis.push({ papelMembroId: secretario.id, minimo: 0, maximo: 1 });
            
            setForm(prev => ({ 
                ...prev, 
                regrasVinculos, 
                regrasPapeis,
                nome: prev.categoria ? getCategoriaLabel(prev.categoria) + ' - Básico' : ''
            }));
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!form.nome || !form.tipoBancaId) {
            toast.error('Preencha os campos obrigatórios');
            return;
        }

        if (form.regrasVinculos.length === 0) {
            toast.error('Adicione pelo menos uma regra de vínculo titular');
            return;
        }

        if (form.dataInicio && form.dataFim && form.dataInicio > form.dataFim) {
            toast.error('Data de início não pode ser posterior à data de fim');
            return;
        }

        try {
            setSalvando(true);
            
            if (form.tipoBancaId === 'TODOS') {
                const formData = { ...form, tipoBancaId: tiposBanca[0]?.id || '' };
                await TemplateComposicaoViewModel.createForAllTipos(formData);
                toast.success('Templates criados para tipos FACIAL, VIRTUAL e RECURSAL com sucesso');
            } else {
                await TemplateComposicaoViewModel.create(form);
                toast.success('Template criado com sucesso');
            }
            
            navigate('/composicoes');
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setSalvando(false);
        }
    };

    const adicionarRegraVinculo = () => {
        if (vinculos.length === 0) return;
        setForm(prev => ({
            ...prev,
            regrasVinculos: [...prev.regrasVinculos, {
                vinculoId: vinculos[0].id,
                minimo: 1,
                maximo: 1
            }]
        }));
    };

    const removerRegraVinculo = (index: number) => {
        setForm(prev => ({
            ...prev,
            regrasVinculos: prev.regrasVinculos.filter((_, i) => i !== index)
        }));
    };

    const adicionarRegraPapel = () => {
        if (papeis.length === 0) return;
        
        // Encontrar papéis que ainda não foram adicionados
        const papeisJaAdicionados = form.regrasPapeis.map(r => r.papelMembroId);
        const papelDisponivel = papeis.find(p => !papeisJaAdicionados.includes(p.id));
        
        if (!papelDisponivel) {
            toast.warning('Todos os papéis já foram adicionados');
            return;
        }
        
        setForm(prev => ({
            ...prev,
            regrasPapeis: [...prev.regrasPapeis, {
                papelMembroId: papelDisponivel.id,
                minimo: 0,
                maximo: 2
            }]
        }));
    };

    const removerRegraPapel = (index: number) => {
        setForm(prev => ({
            ...prev,
            regrasPapeis: prev.regrasPapeis.filter((_, i) => i !== index)
        }));
    };

    const getCategoriaLabel = (categoria: string) => {
        const labels: Record<string, string> = {
            'LEGADO': 'Legado',
            'ALUNO_TECNICO': 'Aluno Técnico',
            'ALUNO_GRADUACAO': 'Aluno Graduação',
            'ALUNO_POS': 'Aluno Pós-Graduação',
            'SERVIDOR': 'Servidor',
            'PROCURADOR': 'Procurador',
            'DOCENTE': 'Docente'
        };
        return labels[categoria] || categoria;
    };

    const aplicarSugestaoNome = () => {
        setForm(prev => ({ ...prev, nome: getCategoriaLabel(prev.categoria) + ' - Básico' }));
    };

    return (
        <div className="min-vh-100" style={{ backgroundColor: '#fafafa' }}>
            <Container fluid className="py-5">
                <div className="mb-5">
                    <Joyride
                        steps={TOUR_STEPS}
                        run={runTour}
                        continuous
                        showSkipButton
                        showProgress
                        scrollToFirstStep
                        callback={handleTourCallback}
                        locale={{ back: 'Voltar', close: 'Fechar', last: 'Finalizar', next: 'Próximo', skip: 'Pular tour' }}
                        styles={{ options: { primaryColor: '#c00', zIndex: 10000 } }}
                    />
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h1 className="fw-light mb-2" style={{ fontSize: '2rem', color: '#1a1a1a' }}>Novo Template</h1>
                            <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>Crie um novo formato de composição de banca</p>
                        </div>
                        <button
                            onClick={() => setRunTour(true)}
                            style={{
                                background: 'transparent',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                color: '#666',
                            }}
                            title="Iniciar tour guiado"
                        >
                            <BsQuestionCircle /> Ajuda
                        </button>
                    </div>
                </div>

                <Form onSubmit={handleSubmit}>
                    <div data-tour="info-basicas" className="bg-white rounded-3 p-4 mb-4" style={{ border: '1px solid #e5e7eb' }}>
                        <h5 className="mb-4 fw-medium" style={{ color: '#1e293b', fontSize: '1.1rem' }}>Informações Básicas</h5>
                        <Row className="g-4">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>Categoria *</Form.Label>
                                    <Form.Select
                                        ref={categoriaRef}
                                        value={form.categoria}
                                        onChange={(e) => {
                                            const novaCategoria = e.target.value;
                                            setForm({...form, categoria: novaCategoria, nome: novaCategoria ? getCategoriaLabel(novaCategoria) + ' - Básico' : ''});
                                        }}
                                        required
                                        className="border-0"
                                        style={{ 
                                            backgroundColor: '#f8fafc', 
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '1rem',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <option value="">Selecione uma categoria...</option>
                                        <option value="LEGADO">Legado</option>
                                        <option value="ALUNO_TECNICO">Aluno Técnico</option>
                                        <option value="ALUNO_GRADUACAO">Aluno Graduação</option>
                                        <option value="ALUNO_POS">Aluno Pós-Graduação</option>
                                        <option value="SERVIDOR">Servidor</option>
                                        <option value="PROCURADOR">Procurador</option>
                                        <option value="DOCENTE">Docente</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>Nome *</Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <Form.Control
                                            type="text"
                                            value={form.nome}
                                            onChange={(e) => setForm({...form, nome: e.target.value})}
                                            required
                                            className="border-0"
                                            style={{ 
                                                backgroundColor: '#f8fafc', 
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                paddingRight: form.categoria ? '140px' : '16px'
                                            }}
                                        />
                                        {form.categoria && (
                                            <button
                                                type="button"
                                                onClick={aplicarSugestaoNome}
                                                className="btn btn-link"
                                                style={{
                                                    position: 'absolute',
                                                    right: '8px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    fontSize: '0.8rem',
                                                    color: '#64748b',
                                                    textDecoration: 'none',
                                                    padding: '4px 8px'
                                                }}
                                            >
                                                <i className="bi bi-lightbulb me-1"></i>
                                                {getCategoriaLabel(form.categoria)} - Básico
                                            </button>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>Tipo de Banca *</Form.Label>
                                    <Form.Select
                                        value={form.tipoBancaId}
                                        onChange={(e) => setForm({...form, tipoBancaId: e.target.value})}
                                        required
                                        className="border-0"
                                        style={{ 
                                            backgroundColor: '#f8fafc', 
                                            borderRadius: '8px',
                                            padding: '12px 16px'
                                        }}
                                    >
                                        <option value="TODOS">Todos</option>
                                        {tiposBanca.map(tipo => (
                                            <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>Descrição</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={form.descricao}
                                        onChange={(e) => setForm({...form, descricao: e.target.value})}
                                        placeholder="Conforme determinado por portaria x..."
                                        className="border-0"
                                        style={{ 
                                            backgroundColor: '#f8fafc', 
                                            borderRadius: '8px',
                                            padding: '12px 16px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    <div data-tour="regras-vinculos" className="bg-white rounded-3 p-4 mb-4" style={{ border: '1px solid #e5e7eb' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0 fw-medium" style={{ color: '#1e293b', fontSize: '1.1rem' }}>Regras de Vínculos Titulares</h5>
                            <button
                                type="button"
                                className="btn border-0 px-3 py-2"
                                style={{ 
                                    backgroundColor: '#2563eb', 
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    fontWeight: '500'
                                }}
                                onClick={adicionarRegraVinculo}
                            >
                                <i className="bi bi-plus me-2"></i>Adicionar
                            </button>
                        </div>
                        {form.regrasVinculos.length === 0 ? (
                            <div className="text-center py-4" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                                <div className="mb-2" style={{ color: '#64748b', fontSize: '2rem' }}>
                                    <i className="bi bi-info-circle"></i>
                                </div>
                                <p className="text-muted mb-0">Nenhuma regra adicionada. Clique em "Adicionar" para criar uma regra.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {form.regrasVinculos.map((regra, index) => (
                                    <div key={index} className="p-3 rounded-2" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <Row className="g-3 align-items-end">
                                            <Col md={5}>
                                                <Form.Group>
                                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.85rem' }}>Vínculo</Form.Label>
                                                    <Form.Select
                                                        value={regra.vinculoId}
                                                        onChange={(e) => {
                                                            const novasRegras = [...form.regrasVinculos];
                                                            novasRegras[index].vinculoId = e.target.value;
                                                            setForm({...form, regrasVinculos: novasRegras});
                                                        }}
                                                        className="border-0"
                                                        style={{ 
                                                            backgroundColor: 'white', 
                                                            borderRadius: '6px',
                                                            padding: '8px 12px'
                                                        }}
                                                    >
                                                        {vinculos.map(v => (
                                                            <option key={v.id} value={v.id}>{v.nome}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.85rem' }}>Mínimo</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={regra.minimo}
                                                        onChange={(e) => {
                                                            const novasRegras = [...form.regrasVinculos];
                                                            novasRegras[index].minimo = parseInt(e.target.value) || 0;
                                                            setForm({...form, regrasVinculos: novasRegras});
                                                        }}
                                                        className="border-0"
                                                        style={{ 
                                                            backgroundColor: 'white', 
                                                            borderRadius: '6px',
                                                            padding: '8px 12px'
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.85rem' }}>Máximo</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={regra.maximo}
                                                        onChange={(e) => {
                                                            const novasRegras = [...form.regrasVinculos];
                                                            novasRegras[index].maximo = parseInt(e.target.value) || 0;
                                                            setForm({...form, regrasVinculos: novasRegras});
                                                        }}
                                                        className="border-0"
                                                        style={{ 
                                                            backgroundColor: 'white', 
                                                            borderRadius: '6px',
                                                            padding: '8px 12px'
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={1}>
                                                <button
                                                    type="button"
                                                    className="btn p-2 border-0"
                                                    style={{ 
                                                        backgroundColor: '#fee2e2',
                                                        color: '#dc2626',
                                                        borderRadius: '6px',
                                                        width: '36px',
                                                        height: '36px'
                                                    }}
                                                    onClick={() => removerRegraVinculo(index)}
                                                >
                                                    <i className="bi bi-trash" style={{ fontSize: '0.85rem' }}></i>
                                                </button>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div data-tour="regras-papeis" className="bg-white rounded-3 p-4 mb-4" style={{ border: '1px solid #e5e7eb' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0 fw-medium" style={{ color: '#1e293b', fontSize: '1.1rem' }}>Regras de Papéis</h5>
                            <button
                                type="button"
                                className="btn border-0 px-3 py-2"
                                style={{ 
                                    backgroundColor: '#2563eb', 
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    fontWeight: '500'
                                }}
                                onClick={adicionarRegraPapel}
                            >
                                <i className="bi bi-plus me-2"></i>Adicionar
                            </button>
                        </div>
                        {form.regrasPapeis.length === 0 ? (
                            <div className="text-center py-4" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                                <div className="mb-2" style={{ color: '#64748b', fontSize: '2rem' }}>
                                    <i className="bi bi-info-circle"></i>
                                </div>
                                <p className="text-muted mb-0">Nenhuma regra adicionada. Clique em "Adicionar" para criar uma regra.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {form.regrasPapeis.map((regra, index) => (
                                    <div key={index} className="p-3 rounded-2" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <Row className="g-3 align-items-end">
                                            <Col md={5}>
                                                <Form.Group>
                                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.85rem' }}>Papel</Form.Label>
                                                    <Form.Select
                                                        value={regra.papelMembroId}
                                                        onChange={(e) => {
                                                            const novasRegras = [...form.regrasPapeis];
                                                            novasRegras[index].papelMembroId = e.target.value;
                                                            setForm({...form, regrasPapeis: novasRegras});
                                                        }}
                                                        className="border-0"
                                                        style={{ 
                                                            backgroundColor: 'white', 
                                                            borderRadius: '6px',
                                                            padding: '8px 12px'
                                                        }}
                                                    >
                                                        {papeis.map(p => {
                                                            const papelJaUsado = form.regrasPapeis.some((r, i) => i !== index && r.papelMembroId === p.id);
                                                            return (
                                                                <option key={p.id} value={p.id} disabled={papelJaUsado}>
                                                                    {p.nome}{papelJaUsado ? ' (já adicionado)' : ''}
                                                                </option>
                                                            );
                                                        })}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.85rem' }}>Mínimo</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={regra.minimo}
                                                        onChange={(e) => {
                                                            const novasRegras = [...form.regrasPapeis];
                                                            novasRegras[index].minimo = parseInt(e.target.value) || 0;
                                                            setForm({...form, regrasPapeis: novasRegras});
                                                        }}
                                                        className="border-0"
                                                        style={{ 
                                                            backgroundColor: 'white', 
                                                            borderRadius: '6px',
                                                            padding: '8px 12px'
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.85rem' }}>Máximo</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={regra.maximo}
                                                        onChange={(e) => {
                                                            const novasRegras = [...form.regrasPapeis];
                                                            novasRegras[index].maximo = parseInt(e.target.value) || 0;
                                                            setForm({...form, regrasPapeis: novasRegras});
                                                        }}
                                                        className="border-0"
                                                        style={{ 
                                                            backgroundColor: 'white', 
                                                            borderRadius: '6px',
                                                            padding: '8px 12px'
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={1}>
                                                <button
                                                    type="button"
                                                    className="btn p-2 border-0"
                                                    style={{ 
                                                        backgroundColor: '#fee2e2',
                                                        color: '#dc2626',
                                                        borderRadius: '6px',
                                                        width: '36px',
                                                        height: '36px'
                                                    }}
                                                    onClick={() => removerRegraPapel(index)}
                                                >
                                                    <i className="bi bi-trash" style={{ fontSize: '0.85rem' }}></i>
                                                </button>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div data-tour="vigencia" className="bg-white rounded-3 p-4 mb-4" style={{ border: '1px solid #e5e7eb' }}>
                        <div className="d-flex align-items-center mb-3">
                            <Form.Check
                                type="checkbox"
                                id="checkbox-expiracao"
                                checked={mostrarExpiracao}
                                onChange={(e) => {
                                    setMostrarExpiracao(e.target.checked);
                                    if (!e.target.checked) {
                                        setForm(prev => ({ ...prev, dataInicio: undefined, dataFim: undefined }));
                                    }
                                }}
                                label="Definir período de vigência (expiração)"
                                className="fw-medium"
                                style={{ color: '#374151', fontSize: '0.95rem' }}
                            />
                        </div>
                        {mostrarExpiracao && (
                            <Row className="g-4 mt-2">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>Data de Início da Vigência</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={form.dataInicio || ''}
                                            onChange={(e) => setForm({...form, dataInicio: e.target.value || undefined})}
                                            className="border-0"
                                            style={{ 
                                                backgroundColor: '#f8fafc', 
                                                borderRadius: '8px',
                                                padding: '12px 16px'
                                            }}
                                        />
                                        <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            Deixe em branco para iniciar imediatamente
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-medium mb-2" style={{ color: '#374151', fontSize: '0.9rem' }}>Data de Fim da Vigência</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={form.dataFim || ''}
                                            onChange={(e) => setForm({...form, dataFim: e.target.value || undefined})}
                                            className="border-0"
                                            style={{ 
                                                backgroundColor: '#f8fafc', 
                                                borderRadius: '8px',
                                                padding: '12px 16px'
                                            }}
                                        />
                                        <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            Deixe em branco para não expirar
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </div>

                    <div data-tour="botoes-acao" className="d-flex justify-content-end gap-3 mt-5">
                        <button
                            type="button"
                            className="btn px-4 py-2 border-0"
                            style={{
                                backgroundColor: '#f8fafc',
                                color: '#64748b',
                                borderRadius: '8px',
                                fontWeight: '500'
                            }}
                            onClick={() => navigate('/composicoes')}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn px-4 py-2 border-0"
                            style={{
                                backgroundColor: salvando ? '#94a3b8' : '#2563eb',
                                color: 'white',
                                borderRadius: '8px',
                                fontWeight: '500'
                            }}
                            disabled={salvando}
                        >
                            {salvando ? 'Salvando...' : (form.tipoBancaId === 'TODOS' ? 'Criar Templates para Todos' : 'Criar Template')}
                        </button>
                    </div>
                </Form>
            </Container>
        </div>
    );
};

export default NovoTemplate;
